/**
 * /cricket/voice-analytics — platform-admin view of voice scoring usage.
 *
 * T127: surfaces who's using voice, how often the AI fallback fires,
 * which words get mis-heard, and per-day usage trend. Feeds product
 * decisions on STT improvements, vocab expansion, and provider choice.
 *
 * Restricted to is_staff via the backend permission gate; this page
 * doesn't add a separate client-side check beyond that.
 */
import { useEffect, useState } from "react";
import { Mic, AlertTriangle, BarChart3, RefreshCw } from "lucide-react";
import api from "../../api/axios";


function StatCard({ label, value, sub }) {
  return (
    <div className="bg-bg-secondary/40 border border-border-default rounded-lg px-3.5 py-2.5">
      <div className="text-[10px] uppercase tracking-wider text-text-secondary mb-0.5">{label}</div>
      <div className="text-xl font-semibold text-text-primary">{value}</div>
      {sub && <div className="text-[11px] text-text-secondary mt-0.5">{sub}</div>}
    </div>
  );
}


function pct(n) {
  if (n == null || isNaN(n)) return "—";
  return `${(n * 100).toFixed(1)}%`;
}

function fmtNum(n) {
  if (n == null) return "—";
  return n.toLocaleString();
}

function fmtMs(ms) {
  if (!ms) return "—";
  return `${(ms / 1000).toFixed(1)}s`;
}


export default function VoiceAnalytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [sinceDays, setSinceDays] = useState(30);

  const reload = async () => {
    setLoading(true); setErr("");
    try {
      const r = await api.get(`/api/admin/voice-analytics/?since_days=${sinceDays}`);
      setData(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not load.");
    } finally { setLoading(false); }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [sinceDays]);

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-1">
        <h2 className="text-xl font-bold flex items-center gap-2">
          <Mic className="w-5 h-5" /> Voice scoring — analytics
        </h2>
        <div className="flex items-center gap-2">
          <select
            value={sinceDays}
            onChange={(e) => setSinceDays(parseInt(e.target.value, 10))}
            className="bg-bg-secondary border border-border-default rounded px-2 py-1 text-xs"
          >
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </select>
          <button
            onClick={reload}
            className="bg-bg-secondary border border-border-default rounded px-2 py-1 text-xs flex items-center gap-1.5"
            title="Refresh"
          >
            <RefreshCw className="w-3 h-3" /> Refresh
          </button>
        </div>
      </div>
      <p className="text-sm text-text-secondary mb-5">
        Usage, accuracy signals, and mishearings across all voice-coded balls.
      </p>

      {loading ? (
        <div className="text-text-secondary text-sm">Loading…</div>
      ) : err ? (
        <div className="bg-button-primary/10 border border-button-primary/30 rounded-lg px-3.5 py-2.5 text-sm text-button-primary flex items-center gap-2">
          <AlertTriangle className="w-4 h-4" /> {err}
        </div>
      ) : !data ? null : (
        <>
          {/* Headline numbers */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            <StatCard label="Utterances" value={fmtNum(data.total_utterances)} sub={`last ${data.since_days}d`} />
            <StatCard label="Matches with voice" value={fmtNum(data.matches_used_voice)} />
            <StatCard label="Unique users" value={fmtNum(data.unique_users)} sub="top-20 sampled" />
            <StatCard label="Operator corrections" value={fmtNum(data.corrections_total)} sub={`${pct(data.total_utterances ? data.corrections_total / data.total_utterances : 0)} of utterances`} />
            <StatCard label="Avg duration" value={fmtMs(data.avg_duration_ms)} />
            <StatCard label="Avg size" value={data.avg_size_bytes ? `${Math.round(data.avg_size_bytes / 1024)} KB` : "—"} />
            <StatCard label="Avg confidence" value={data.avg_confidence != null ? data.avg_confidence.toFixed(2) : "—"} sub="Whisper soft signal" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Per-user usage */}
            <section>
              <div className="text-[10px] uppercase tracking-wider text-purple-300 font-semibold mb-2 flex items-center gap-1.5">
                <BarChart3 className="w-3 h-3" /> Top users (utterances)
              </div>
              {data.per_user.length === 0 ? (
                <div className="text-xs text-text-secondary">No usage yet.</div>
              ) : (
                <table className="w-full text-xs">
                  <thead className="text-text-secondary border-b border-border-default">
                    <tr>
                      <th className="text-left py-1.5">Email</th>
                      <th className="text-right py-1.5">N</th>
                      <th className="text-right py-1.5">Corrections</th>
                      <th className="text-right py-1.5">Avg conf</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.per_user.map((r) => (
                      <tr key={r.user_id} className="border-b border-border-default/40">
                        <td className="py-1 text-text-primary truncate max-w-[200px]">{r.email}</td>
                        <td className="py-1 text-right text-text-primary">{r.n}</td>
                        <td className="py-1 text-right text-text-secondary">
                          {r.corrected} <span className="text-text-secondary/60">({pct(r.correction_rate)})</span>
                        </td>
                        <td className="py-1 text-right text-text-secondary">{r.avg_conf != null ? r.avg_conf.toFixed(2) : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </section>

            {/* Top mishearings — most actionable for STT_ALIASES expansion */}
            <section>
              <div className="text-[10px] uppercase tracking-wider text-purple-300 font-semibold mb-2 flex items-center gap-1.5">
                <AlertTriangle className="w-3 h-3" /> Top mishearings (heard → intended)
              </div>
              {data.top_mishearings.length === 0 ? (
                <div className="text-xs text-text-secondary">No corrections collected yet.</div>
              ) : (
                <table className="w-full text-xs">
                  <thead className="text-text-secondary border-b border-border-default">
                    <tr>
                      <th className="text-left py-1.5">Heard</th>
                      <th className="text-left py-1.5">Intended</th>
                      <th className="text-right py-1.5">Count</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.top_mishearings.map((r) => (
                      <tr key={`${r.heard}-${r.intended}`} className="border-b border-border-default/40">
                        <td className="py-1 font-mono text-text-primary">{r.heard}</td>
                        <td className="py-1 font-mono text-emerald-300">{r.intended}</td>
                        <td className="py-1 text-right text-text-secondary">{r.count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </section>

            {/* STT model split */}
            <section>
              <div className="text-[10px] uppercase tracking-wider text-purple-300 font-semibold mb-2">
                STT model split
              </div>
              <table className="w-full text-xs">
                <tbody>
                  {data.per_stt_model.map((r) => {
                    const share = data.total_utterances ? r.n / data.total_utterances : 0;
                    return (
                      <tr key={r.stt_model} className="border-b border-border-default/40">
                        <td className="py-1 text-text-primary">{r.stt_model || "(unknown)"}</td>
                        <td className="py-1 text-right text-text-secondary">
                          {r.n} <span className="text-text-secondary/60">({pct(share)})</span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </section>

            {/* Parsed kinds — what voice did at the action level */}
            <section>
              <div className="text-[10px] uppercase tracking-wider text-purple-300 font-semibold mb-2">
                Parse outcomes
              </div>
              <table className="w-full text-xs">
                <tbody>
                  {data.per_parsed_kind.map((r) => (
                    <tr key={r.parsed_kind} className="border-b border-border-default/40">
                      <td className="py-1 text-text-primary capitalize">{r.parsed_kind}</td>
                      <td className="py-1 text-right text-text-secondary">{r.n}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>

            {/* Per-day trend — text bars instead of pulling a chart lib */}
            <section className="md:col-span-2">
              <div className="text-[10px] uppercase tracking-wider text-purple-300 font-semibold mb-2">
                Daily utterance volume
              </div>
              {(() => {
                const max = Math.max(1, ...data.per_day.map((d) => d.n));
                return (
                  <div className="space-y-0.5">
                    {data.per_day.map((d) => (
                      <div key={d.d} className="flex items-center gap-2 text-[11px]">
                        <span className="font-mono text-text-secondary w-20 shrink-0">{d.d}</span>
                        <div className="flex-1 bg-bg-secondary/40 rounded h-3 relative">
                          <div
                            className="absolute inset-y-0 left-0 bg-purple-500/70 rounded"
                            style={{ width: `${(d.n / max) * 100}%` }}
                          />
                        </div>
                        <span className="font-mono text-text-primary w-10 text-right">{d.n}</span>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </section>
          </div>
        </>
      )}
    </div>
  );
}
